import { formatTime } from "@/lib/format-data";
import { startZoneSuffix } from "@/lib/sms-templates";

import type { PublicEventTiny } from "@/types/event";

/** A visit mixing virtual and in-person services reads as in person, so a
 * patient who chose telehealth would be asked in. Self-scheduling books the
 * two as separate appointments; staff can still mix them. */
export const MIXED_DELIVERY_MESSAGE =
  "Telehealth and in-person visits are booked separately. Book them as two appointments.";

/** Every service in the cart is virtual: the visit is by video, booked at
 * whichever location its time is found, so the patient picks no location. */
export const isVirtualServiceCart = (
  services: { isVirtual?: boolean }[] | undefined,
): boolean =>
  !!services?.length && services.every((service) => service.isVirtual === true);

/** The cart puts telehealth and in-person services in one visit away from a
 * virtual location, which self-scheduling refuses. The service cards stop it,
 * but a `?serviceId=` link or a care-plan step can still fill the cart so. */
export const cartMixesDelivery = (
  services: { isVirtual?: boolean }[] | undefined,
  location: { isVirtual?: boolean } | undefined,
): boolean =>
  !location?.isVirtual &&
  new Set(services?.map((service) => service.isVirtual === true)).size > 1;

/** The visit's main services. Add-ons follow the service they're attached to. */
const mainServices = (appointment: PublicEventTiny) =>
  (appointment.segments || []).flatMap((segment) =>
    !segment.serviceAddonId && segment.service ? [segment.service] : [],
  );

/** Every main service on this visit is virtual, so a reschedule searches every
 * location again, as a new booking would. */
export const isVirtualServiceVisit = (appointment: PublicEventTiny): boolean =>
  isVirtualServiceCart(mainServices(appointment));

/** Staff booked virtual and in-person services together on this visit, away
 * from a virtual location, which self-scheduling can't reschedule; the patient
 * calls instead. */
export const mixesDelivery = (appointment: PublicEventTiny): boolean =>
  !appointment.location?.isVirtual &&
  new Set(
    mainServices(appointment).map((service) => service.isVirtual === true),
  ).size > 1;

/** What the patient reads for where a video visit happens. */
export const VIRTUAL_VISIT_DETAIL = "By video. We'll text you how to join.";

/** A video visit's time as its texts will give it, in the zone of the location
 * it's booked at, with this page's zone beside it when they differ:
 * "8:00 AM PDT (10:00 AM CDT)". */
export const formatVisitTime = (
  start: Date,
  visitZone: string | undefined,
  pageZone: string,
): string => {
  if (!visitZone || visitZone === pageZone) return formatTime(start);
  const visitTime = `${formatTime(start, true, visitZone)}${startZoneSuffix(start, visitZone, true)}`;
  const pageTime = `${formatTime(start, true, pageZone)}${startZoneSuffix(start, pageZone, true)}`;
  return visitTime === pageTime ? visitTime : `${visitTime} (${pageTime})`;
};
